import { normalizeSlotIdentifier } from "./slot";
import { buildLocationKey } from "./staticPickWall";

/**
 * Pure slot-resolution helpers shared by the inward and cycle-count flows.
 *
 * The scanner replaces "tap a slot in the grid" with "scan the slot barcode".
 * A scanned/typed identifier (`4.1.2`, `4,1,2`, `4-1-2`) is normalized and
 * compared against gate/slot coordinates using the same `x,y,z` location key
 * the desktop builds (`buildLocationKey`).
 */

export interface Coord {
  x: number;
  y: number;
  z: number;
}

/** Parse a scanned slot barcode into the backend "x,y,z" location key, or null. */
export function coordKeyFromScan(
  scanned: string | null | undefined
): string | null {
  const normalized = normalizeSlotIdentifier(scanned);
  const parts = normalized.split("-");
  if (parts.length !== 3) {
    return null;
  }
  const nums = parts.map((part) => Number(part));
  if (nums.some((n) => !Number.isFinite(n))) {
    return null;
  }
  return buildLocationKey(nums[0], nums[1], nums[2]);
}

const keyOf = (c?: Partial<Coord> | null): string | null =>
  buildLocationKey(c?.x as number, c?.y as number, c?.z as number);

// ── Inward ──

export type InwardGateMatch<G, S> =
  | { kind: "dynamic"; gate: G }
  | { kind: "static"; slot: S }
  | null;

/**
 * Resolve a scanned slot to an inward target: first a dynamic gate from the
 * inward stations, then a static pick-wall slot.
 */
export function resolveInwardGate<G extends Coord, S extends Coord>(
  scanned: string,
  dynamicGates: G[],
  staticSlots: S[]
): InwardGateMatch<G, S> {
  const key = coordKeyFromScan(scanned);
  if (!key) {
    return null;
  }
  const gate = dynamicGates.find((g) => keyOf(g) === key);
  if (gate) {
    return { kind: "dynamic", gate };
  }
  const slot = staticSlots.find((s) => keyOf(s) === key);
  if (slot) {
    return { kind: "static", slot };
  }
  return null;
}

// ── Cycle count ──

export interface CycleTaskLike {
  cycleCountTask?: { gateLocation?: Partial<Coord> };
  isComplete?: boolean;
}

export type CycleTargetMatch<T, S> =
  | { kind: "dynamic"; task: T }
  | { kind: "static"; slot: S; tasks: T[] }
  | null;

/**
 * Resolve a scanned slot to a cycle-count target. A configured static slot uses
 * the static (per-task, zero-delta) flow; otherwise the first pending dynamic
 * task at that location drives the single-task flow. Returns null when nothing
 * actionable sits at the scanned slot.
 */
export function resolveCycleCountTarget<T extends CycleTaskLike, S extends Coord>(
  scanned: string,
  tasks: T[],
  staticSlots: S[]
): CycleTargetMatch<T, S> {
  const key = coordKeyFromScan(scanned);
  if (!key) {
    return null;
  }

  const pendingAtKey = tasks.filter(
    (t) => keyOf(t.cycleCountTask?.gateLocation) === key && t.isComplete !== true
  );

  const staticSlot = staticSlots.find((s) => keyOf(s) === key);
  if (staticSlot) {
    return pendingAtKey.length > 0
      ? { kind: "static", slot: staticSlot, tasks: pendingAtKey }
      : null;
  }

  return pendingAtKey.length > 0
    ? { kind: "dynamic", task: pendingAtKey[0] }
    : null;
}
